import { useCallback, useEffect, useRef, useState } from "react";
import type { UIEvent } from "react";
import { getProjectUpvoters } from "~/services/projects";
import { avatarUrl } from "~/utils/app";
import styles from "./ProjectPopup.module.css";
import type { ProjectUpvoter } from "~/types/project";

/** 收起态显示的头像数，超出显示「…」 */
const COLLAPSED_LIMIT = 8;
/** 展开态的每页条数：一页够铺满 3 行，滚到底再取下一页 */
const EXPANDED_PAGE_SIZE = 60;

// 追加新一页时按 id 去重：翻页期间有人顶过会导致分页错位，同一个人可能被返回两次
const mergeUsers = (prev: ProjectUpvoter[], next: ProjectUpvoter[]) => {
  const seen = new Set(prev.map((user) => user.id));
  return [...prev, ...next.filter((user) => !seen.has(user.id))];
};

type ProjectUpvotersProps = {
  projectId: string;
  /** 项目的顶数（project.upvotes_count）：为 0 时整排不显示 */
  count?: number;
};

// 弹窗里「已有 N 人顶过」上方的头像排：默认 8 个（最近顶的在前），
// 点「…」就地展开成多行显示全部，展开后最多 3 行、再多在头像区内滚动
const ProjectUpvoters = ({ projectId, count }: ProjectUpvotersProps) => {
  const [list, setList] = useState<ProjectUpvoter[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // 展开后调大每页条数；收起时不改回去，避免同一次会话里翻页的页大小前后不一致
  const [pageSize, setPageSize] = useState(COLLAPSED_LIMIT);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // 取第一页的响应可能乱序（刚顶完 / 刚展开），用请求序号丢弃过期响应
  const requestIdRef = useRef(0);
  // 取下一页的并发保护：滚动事件一帧里会触发多次，用 state 判断来不及（要等下一次渲染），
  // 会把同一个 page 请求好几遍，所以用 ref 同步挡掉
  const loadingMoreRef = useRef(false);

  const hasUpvotes = Boolean(count && count > 0);

  // 取第一页：收起态 8 个、展开态 60 个。顶数变化（自己刚顶完）也重取一次，
  // 后端按顶的时间倒序返回，所以自己会排到最前面
  useEffect(() => {
    if (!hasUpvotes) {
      setList([]);
      setNextPage(null);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    getProjectUpvoters(projectId, { page: 1, perPage: pageSize })
      .then((res) => {
        if (requestIdRef.current !== requestId) return;
        setList(res?.users || []);
        setNextPage(res?.pager?.next_page ?? null);
      })
      // 失败就静默忽略：保留已经显示的头像，不影响弹窗其它内容
      .catch(() => {});
  }, [projectId, hasUpvotes, count, pageSize]);

  const handleExpand = () => {
    setExpanded(true);
    // 调大每页条数会触发上面的 effect 重新取第一页
    if (pageSize !== EXPANDED_PAGE_SIZE) setPageSize(EXPANDED_PAGE_SIZE);
  };

  const handleCollapse = () => {
    setExpanded(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  // 取下一页：滚动到底、或加载的内容还没填满滚动区时都会用到
  const loadMore = useCallback(() => {
    if (nextPage == null || loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    getProjectUpvoters(projectId, { page: nextPage, perPage: pageSize })
      .then((res) => {
        const users = res?.users || [];
        if (users.length) setList((prev) => mergeUsers(prev, users));
        setNextPage(res?.pager?.next_page ?? null);
      })
      .catch(() => {})
      .finally(() => {
        loadingMoreRef.current = false;
      });
  }, [projectId, nextPage, pageSize]);

  // 展开态滚到接近底部时取下一页
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 40) return;
    loadMore();
  };

  // 展开态：已加载的还没填满滚动区（没有滚动条就滚不出更多），继续取下一页。
  // 每页条数调小后这一点尤其明显，不然第一页之后就没法再加载了
  useEffect(() => {
    if (!expanded || nextPage == null) return;
    const el = scrollRef.current;
    if (!el || el.scrollHeight > el.clientHeight) return;
    loadMore();
  }, [expanded, nextPage, list, loadMore]);

  if (!hasUpvotes || !list.length) return null;

  const visible = list.slice(0, COLLAPSED_LIMIT);
  // 顶过的人比这一排显示的多，就给个「…」展开入口
  const hasMore = (count || 0) > visible.length;

  const renderAvatar = (user: ProjectUpvoter) => (
    <span
      key={user.id}
      className={styles.popAvasItem}
      title={user.nickname}
    >
      <img
        src={avatarUrl(user, 64)}
        alt={user.nickname}
      />
    </span>
  );

  if (expanded) {
    return (
      <div className={styles.popAvasWrap}>
        <div
          ref={scrollRef}
          className={styles.popAvasScroll}
          onScroll={handleScroll}
        >
          <div className={styles.popAvasGrid}>{list.map(renderAvatar)}</div>
        </div>
        <button
          type="button"
          className={styles.popAvasCollapse}
          onClick={handleCollapse}
        >
          收起
        </button>
      </div>
    );
  }

  return (
    <div className={styles.popAvasWrap}>
      <div className={styles.popAvas}>
        {visible.map(renderAvatar)}
        {hasMore && (
          <button
            type="button"
            className={styles.popAvasMore}
            aria-label="展开全部顶过的人"
            onClick={handleExpand}
          >
            …
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectUpvoters;
